import { ConditionParams } from "@multiweb/clients/dataReader";
import { IAttrValue } from "@multiweb/types/contracts";
import { ExtendedPropertiesType } from "UI/types";
import { IFrame } from "UI/UI/UI/XGControls/IFrame";
import { Event } from "../../../Global/Base/Events";
import { IWorkflowMenuItem } from "../../../OW.Remoting/Remoting/IWorkflowManager";
import { Icon } from "../../UI/UI/FormUI/FormUI";
import { IToolbarButton } from "../../UI/UI/XGControls/Actions/IToolbarButton";

/**
 * Typ akce přidávané do menu
 * Enum je potřeba udržovat i v umístění '/MWCore/TescoSW/UI/UI.React/types/multiweb/index.ts'
 * @includeToDoc
 */
export enum ActionType {
    none,
    custom,

    //standardní akce
    standardDeleteRecord,
    standardDeleteRecords,
    standardEditRecord,
    standardEditRecords,
    standardNewRecord,
    standardCopyRecord,
    standardSaveRecord,
    standardSaveRecordInEditContext,
    standardSaveAll,
    standardCancelChanges,
    standardCancelRecordChanges,
    standardRefreshRecord,
    standardRefreshList,
    standardOpenDetail,
    standardSelect,
    standardSelectRecord,
    standardSelectAll,
    standardDeselectAll,
    standardShowAll,
    standardShowFiltered,
    standardRunMethod,
    standardRunMethodAndClose,
    standardOpenResultForm,
    standardOpenConditionForm,
    standardCloseAndOpenConditionForm,
    standardCloseAfterWF,
    standardBack,
    standardDecrypt,
    standardRowActions,
    standardPreviousRecord,
    standardNextRecord,
    standardMoveRightRecord,
    standardMoveLeftRecord,
    standardMoveRightRecords,
    standardMoveLeftRecords,
    standardPrintAll,
    standardPrintRecord,
    standardExportCSV,
    standardExportExcel,
    standardOpenDetailOnMainForm,
    standardAuditInfoRecord,
    standardShowWFHistory,
    standardShowWFParticipants,
    standardShowWFStatus,
    standardInfoRecord,
    standardFulltextSearch,
    standardDownloadAll,
    standardGraph,
    standardColumnSettings,
    standardRecordsCount,
    standardToggleAggregationRow,
    standardShowSubRecords,
    // calendar
    standardSetToday,
    standardSwitchIntervalDay,
    standardSwitchIntervalWeek,
    standardSwitchIntervalMonth,
    //standardSwitchIntervalYear,
    standardNextInterval,
    standardPreviousInterval,

    // graf
    standardChartName,
    standardMoreSettings,
    standardChartSettings,
    standardImplementationChart,

    // filter
    standardFilter,
    standardFilterList,

    node,
    form,
    parametricForm,
    insert,
    method,
    report,
    reportAndClose,
    workflow,
    help,
    crypto,
    cryptoLabel,
}

/**
 * Kontext, ve kterém má akce smysl
 */
export enum ActionContext {
    /**
     * Nedefinováno
     */
    none,
    /**
     * Akci je potřeba spouštět nad aplikačním záznamem
     */
    objectAction,
    /**
     * Akce se vztahuje k framu jako celku
     */
    frameAction,
    /**
     * Akce se vztahuje na kontextovou nabídku
     */
    contextAction,
}

/**
 * Režim výběru záznamů
 */
export enum SelectionType {
    /**
     * Pro akci je vybrán pouze jeden datový záznam
     */
    single = 1 << 0,
    /**
     * Pro akci je vybráno více datových záznamů
     */
    multi = 1 << 1,
    /**
     * Pro akci je vybrán jeden nebo více záznamů
     */
    all = single | multi,
}

/**
 * Základní rozhraní pro data akce ActionWithData
 */
export interface IDataForAction {
    [name: string]: any;
}

/**
 * Určuje formát dat pro grafy použité v ActionWithData
 */
export interface IGraphDataForAction extends IDataForAction {
    chartName: string;
    chartProfileId: IAttrValue;
}

export class IGraphDataForAction {
    public static _properties: (keyof IGraphDataForAction)[] = ["chartName", "chartProfileId"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

export interface IBaseAction {
    readonly id?: string;
    priority: number;
    order: number;
    locKey: string;
    locParams?: string[];
    enabled: boolean;
    enabledVisual: boolean;
    isActive: boolean;
    visible: boolean;
    /**
     * Určuje, zda se jedná o akci, která pracuje s konkrétnímu záznamem nebo s celým framem
     */
    readonly context: ActionContext;
    /**
     * Typ akce určující její účel
     */
    readonly actionType: ActionType;
    /**
     * Určuje, v kterých modech výběru má být akce aktivní
     */
    readonly supportedSelectionModes: SelectionType;

    readonly action: (...params: any[]) => Promise<void>;

    readonly enabledChanged: Event<IBaseAction, boolean>;
    readonly visibilityChanged: Event<IBaseAction, boolean>;
    readonly enabledVisualChanged: Event<IBaseAction, boolean>;
    readonly isActiveChanged: Event<IBaseAction, boolean>;
    readonly interactiveChanged: Event<IBaseAction, boolean>;

    actionSize?: string;
    toolbarButton?: IToolbarButton; //TODO: [WI 16310]
    menuType?: MenuType;
    toMenu?: MenuType;
    masterNode?: string;
    isInNode?: boolean;
    icon?: Icon | string;
    /** Volitelná data přenášená s akcí, typicky umožňují stylování vizuální reprezentace akce nebo její další rozšíření nebo nastavení */
    extendedProperties?: ExtendedPropertiesType;

    dispose(): void;
}

export class IBaseAction {
    private static _properties: (keyof IBaseAction)[] = ["supportedSelectionModes", "enabledChanged", "visibilityChanged", "enabledVisualChanged", "isActiveChanged", "interactiveChanged", "enabled", "enabledVisual", "isActive", "visible", "locKey", "context", "priority", "action", "actionType", "order"];
    private static _methods: (keyof IBaseAction)[] = ["dispose"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Rozhraní dvoustavové akce
 */
export interface ITwoStateAction extends IBaseAction {
    checked: boolean;
}

export class ITwoStateAction {
    private static _properties: (keyof ITwoStateAction)[] = ["checked"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            instance instanceof IBaseAction;
    }
}

/**
 * Rozhraní pro objekt zadaný ID a třídou
 */
export interface IActionContext {
    ids: string[];
    className: string;
    isSelectedAll?: boolean;
    condition?: string;
    conditionParams?: ConditionParams;
}

export class IActionContext {
    private static _properties: (keyof IActionContext)[] = ["ids", "className"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

export interface IAction extends IBaseAction {
    execute: (context?: IActionContext) => Promise<void>;
}

export class IAction {
    private static _methods: (keyof IAction)[] = ["execute"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IBaseAction;
    }
}

/**
 * Rozšiřuje IAction o možnost přidat libovolná data
 */
export interface IActionWithData extends IAction {
    data: IDataForAction;
}

export class IActionWithData {
    public static _properties: (keyof IActionWithData)[] = ["data"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            instance instanceof IBaseAction;
    }
}

export interface IWFAction extends IAction {
    caption: string;
    wfMenuItem: IWorkflowMenuItem;
}

export class IWFAction {
    private static _properties: (keyof IWFAction)[] = ["caption", "wfMenuItem"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            instance instanceof IAction;
    }
}

export interface IMultiObjectAction extends IAction {
    readonly mainAction: IAction;
    owner: IFrame;
}

export class IMultiObjectAction {
    private static _properties = ["mainAction"];
    private static _methods: string[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IAction;
    }
}

/**
 * TODO-rites: asi iface k ničemu...?
 */
//export interface IPostponedRequirableAction extends IAction {
//    readonly validatePostReq: boolean;
//}

//export class IPostponedRequirableAction {
//    private static _properties = ["validatePostReq"];
//    private static _methods: string[] = [];

//    public static [Symbol.hasInstance](instance: any) {
//        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
//            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IAction;
//    }
//}

/**
 * Typ menu v toolbaru
 */
export enum MenuType { mtForm, mtPrint, mtActions, mtConditionForm, mtWorkflow, mtHelp, mtNone, mtRecord, mtFormControl, mtCancelSave, mtStandardAction, mtRecordNavigation, mtFilter, mtInfo, mtSettings, mtFrameMenu }
